import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, IconButton, Tabs, Tab, TextField, Avatar, CircularProgress,
Button, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Switch, FormControlLabel, Card, CardContent,
Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {LocalizationProvider, DatePicker} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ArrowBack as ArrowBackIcon, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { type UserSettings } from '@/api/userSettings';
import { useAppStore } from '@/store';
import DialogWrapper from '@/components/DialogWrapper';
import PermissionsList from '@/components/PermissionsList';
import { useCurrentUserSettingsQuery, useMyModelsUsageQuery, useMyTotalMessagesQuery,
  useMyTotalCostsQuery, useMyMonthlyCostsQuery
 } from '@/features/userSettings/hooks/useUserSettingsQuery';
import { useUpdateCurrentUserSettingsMutation } from '@/features/userSettings/hooks/useUserSettingsMutations';
import {
  usePermissionRequestsQuery,
  useAccessiblePermissionsQuery,
  useRequestablePermissionsQuery,
} from '@/features/permissionRequests/hooks/usePermissionRequestsQuery';
import { useCreatePermissionRequestBatchMutation } from '@/features/permissionRequests/hooks/usePermissionRequestsMutations';
import { queryKeys } from '@/lib/queryKeys';
import { QueryErrorState, QueryStaleDataWarning } from '@/components/state';
import { getProfileImage, fetchAndCacheProfileImage } from '@/utils/profileImageCache';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-settings-tabpanel-${index}`}
      aria-labelledby={`user-settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `user-settings-tab-${index}`,
    'aria-controls': `user-settings-tabpanel-${index}`,
  };
}

const UserSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState<number>(0);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const user = useAppStore(s => s.user);
  const [cachedProfileImage, setCachedProfileImage] = useState<string | null>(null);
  const lastPictureVersionRef = useRef<number | undefined>(undefined);
  const {
    data: currentUserSettings,
    isPending: loading,
    isError: isUserSettingsError,
    isRefetchError: isCurrentUserSettingsRefetchError,
    error: userSettingsError,
    refetch: refetchCurrentUserSettings,
  } = useCurrentUserSettingsQuery();
  const updateCurrentUserSettingsMutation = useUpdateCurrentUserSettingsMutation();
  const createPermissionRequestBatchMutation = useCreatePermissionRequestBatchMutation();
  
  // Query hooks - enabled when dialogs open
  const [dialogsOpen, setDialogsOpen] = useState({ permissions: false, request: false });
  const { data: accessiblePermissions, isPending: permissionsLoading } = useAccessiblePermissionsQuery(dialogsOpen.permissions);
  const { data: requestablePermissions } = useRequestablePermissionsQuery(dialogsOpen.permissions || dialogsOpen.request);
  const { data: pendingPermissionRequestsData } = usePermissionRequestsQuery('pending', false, dialogsOpen.request);

  const toStartOfDayIso = (date: Date): string => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized.toISOString();
  };

  const toEndOfDayIso = (date: Date): string => {
    const normalized = new Date(date);
    normalized.setHours(23, 59, 59, 999);
    return normalized.toISOString();
  };
  // Date range state - Default: last 30 days
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    date.setHours(0, 0, 0, 0);
    return date;
  };  
  const [startDate, setStartDate] = useState<Date | null>(getDefaultStartDate());
  
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  };
  const [endDate, setEndDate] = useState<Date | null>(getDefaultEndDate());
  
  const dashboardRef = useRef<HTMLDivElement>(null);

  const startDateParam = useMemo(
    () => (startDate ? toStartOfDayIso(startDate) : ''),
    [startDate]
  );
  const endDateParam = useMemo(
    () => (endDate ? toEndOfDayIso(endDate) : ''),
    [endDate]
  );

  const pendingPermissions = useMemo(() => {
    if (!pendingPermissionRequestsData) {
      return [] as Array<{ permission?: string }>;
    }

    if (Array.isArray((pendingPermissionRequestsData as any).requests)) {
      return (pendingPermissionRequestsData as any).requests as Array<{ permission?: string }>;
    }

    if (Array.isArray(pendingPermissionRequestsData as any)) {
      return pendingPermissionRequestsData as unknown as Array<{ permission?: string }>;
    }

    return [] as Array<{ permission?: string }>;
  }, [pendingPermissionRequestsData]);

  const pendingModelIds = useMemo(() => (
    pendingPermissions
      .filter((req) => typeof req.permission === 'string' && req.permission.startsWith('model:'))
      .map((req) => (req.permission as string).split(':', 2)[1])
      .filter(Boolean)
  ), [pendingPermissions]);

  const pendingAgentIds = useMemo(() => (
    pendingPermissions
      .filter((req) => typeof req.permission === 'string' && req.permission.startsWith('agent:'))
      .map((req) => (req.permission as string).split(':', 2)[1])
      .filter(Boolean)
  ), [pendingPermissions]);

  const pendingFeatureIds = useMemo(() => (
    pendingPermissions
      .filter((req) => typeof req.permission === 'string' && req.permission.startsWith('feature:'))
      .map((req) => (req.permission as string).split(':', 2)[1])
      .filter(Boolean)
  ), [pendingPermissions]);
  
  // Permissions state - sync from query hooks when they load
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [availableAgents, setAvailableAgents] = useState<any[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<any[]>([]);
  
  // Request permissions dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [allModels, setAllModels] = useState<any[]>([]);
  const [allAgents, setAllAgents] = useState<any[]>([]);
  const [allFeatures, setAllFeatures] = useState<any[]>([]);
  const [requestedModelIds, setRequestedModelIds] = useState<string[]>([]);
  const [requestedAgentIds, setRequestedAgentIds] = useState<string[]>([]);
  const [requestedFeatureIds, setRequestedFeatureIds] = useState<string[]>([]);
  const [permissionRequestReason, setPermissionRequestReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Custom instructions state
  const [customInstructions, setCustomInstructions] = useState('');
  const [customInstructionsEnabled, setCustomInstructionsEnabled] = useState(false);
  const customInstructionsMaxLength = 2000;
  const savingCustomInstructions = updateCurrentUserSettingsMutation.isPending;
  // Share profile state
  const [shareProfileEnabled, setShareProfileEnabled] = useState(false);
  const [shareProfileDialogOpen, setShareProfileDialogOpen] = useState(false);
  const [pendingShareProfileEnabled, setPendingShareProfileEnabled] = useState(false);
  

  // Model usage data
  const {data: myModelsUsage,isLoading: loadingMyModels,error: errorMyModels} = useMyModelsUsageQuery(startDateParam, endDateParam, 5, !!(startDate && endDate));

  // My Total Messages data
  const {data: myTotalMessages,isLoading: loadingMyTotalMessages,error: errorMyTotalMessages} = useMyTotalMessagesQuery(startDateParam, endDateParam, !!(startDate && endDate));
  
  // My Total Costs data
  const {data: myTotalCosts,isLoading:loadingTotalCosts,error:errorTotalCosts} = useMyTotalCostsQuery(startDateParam, endDateParam, !!(startDate && endDate));

  // My Monthly Costs data
  const {data: myMonthlyCosts,isLoading:loadingMonthlyCosts,error:errorMonthlyCosts} = useMyMonthlyCostsQuery(6);

  useEffect(() => {
    let cancelled = false;

    const ensureImage = async () => {
      if (!user?.id) {
        setCachedProfileImage(null);
        lastPictureVersionRef.current = undefined;
        return;
      }

      const currentVersion = user.pictureVersion;
      const versionChanged =
        typeof currentVersion === 'number' &&
        typeof lastPictureVersionRef.current === 'number' &&
        currentVersion !== lastPictureVersionRef.current;

      lastPictureVersionRef.current = currentVersion;

      if (!versionChanged) {
        const cached = getProfileImage(user.id);
        if (cached) {
          if (!cancelled) {
            setCachedProfileImage(cached);
          }
          return;
        }
      }

      const fetched = await fetchAndCacheProfileImage(user.id, versionChanged);
      if (!cancelled) {
        setCachedProfileImage(fetched || null);
      }
    };

    ensureImage().catch((err) => console.error('Failed to ensure profile image in user settings:', err));

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.pictureVersion]);

  const profileAvatarUrl = cachedProfileImage || user?.picture || undefined;

  useEffect(() => {
    if (!currentUserSettings) {
      return;
    }

    setUserSettings(currentUserSettings);
  }, [currentUserSettings]);

  useEffect(() => {
    if (!currentUserSettings) {
      return;
    }
    // Only sync custom instructions when they change
    setCustomInstructions(currentUserSettings.custom_instructions || '');
    setCustomInstructionsEnabled(currentUserSettings.custom_instructions_enabled || false);
    setShareProfileEnabled(currentUserSettings.share_profile_enabled || false);
  }, [
    currentUserSettings?.custom_instructions,
    currentUserSettings?.custom_instructions_enabled,
    currentUserSettings?.share_profile_enabled,
  ]);

  const queryErrorMessage = isUserSettingsError
    ? (userSettingsError instanceof Error ? userSettingsError.message : 'Failed to load user settings')
    : null;
  const hasCachedUserSettings = Boolean(currentUserSettings || userSettings);
  const showStaleDataWarning = Boolean(isCurrentUserSettingsRefetchError && hasCachedUserSettings && queryErrorMessage);
  const blockingUserSettingsError = hasCachedUserSettings ? null : queryErrorMessage;

  const retryUserSettingsQuery = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.current() });
  };
  
  // Enable accessible permissions query when on Permissions tab
  useEffect(() => {
    setDialogsOpen(prev => ({ ...prev, permissions: tabValue === 1 }));
  }, [tabValue]);
  
  // Sync accessible permissions from query hook
  useEffect(() => {
    if (accessiblePermissions && tabValue === 1) {
      setAvailableModels(accessiblePermissions.models);
      setAvailableAgents(accessiblePermissions.agents);
      setAvailableFeatures(accessiblePermissions.features);
    }
  }, [accessiblePermissions, tabValue]);

  useEffect(() => {
    if (tabValue !== 0) {
      return;
    }

    // Refresh settings when returning to profile tab so stale-warning logic can react
    // to backend refresh failures while preserving cached data in the UI.
    void refetchCurrentUserSettings();
  }, [tabValue, refetchCurrentUserSettings]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Sync requestable permissions from query hook
  useEffect(() => {
    if (requestablePermissions && (tabValue === 1 || requestDialogOpen)) {
      setAllModels(requestablePermissions.models);
      setAllAgents(requestablePermissions.agents);
      setAllFeatures(requestablePermissions.features);
    }
  }, [requestablePermissions, requestDialogOpen, tabValue]);

  useEffect(() => {
    if (!requestDialogOpen) {
      return;
    }

    setRequestedModelIds((prev) => prev.filter((id) => !pendingModelIds.includes(id)));
    setRequestedAgentIds((prev) => prev.filter((id) => !pendingAgentIds.includes(id)));
    setRequestedFeatureIds((prev) => prev.filter((id) => !pendingFeatureIds.includes(id)));
  }, [requestDialogOpen, pendingModelIds, pendingAgentIds, pendingFeatureIds]);
  
  const handleOpenRequestDialog = () => {
    setDialogsOpen(prev => ({ ...prev, request: true }));
    setRequestDialogOpen(true);
  };
  
  const handleSubmitRequest = async () => {
    try {
      setSubmitting(true);
      
      // Build permissions array
      const requestedPermissions = [
        ...requestedModelIds.map(id => `model:${id}`),
        ...requestedAgentIds.map(id => `agent:${id}`),
        ...requestedFeatureIds.map(id => `feature:${id}`)
      ];
      
      if (requestedPermissions.length === 0) {
        setSnackbarMessage('Please select at least one permission to request');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setSubmitting(false);
        return;
      }

      const trimmedReason = permissionRequestReason.trim();
      if (!trimmedReason) {
        setSnackbarMessage('Please provide a reason for your request');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setSubmitting(false);
        return;
      }
      
      // Submit to backend using batch API mutation
      await createPermissionRequestBatchMutation.mutateAsync({
        requested_permissions: requestedPermissions,
        reason: trimmedReason,
      });
      
      // Reset and close dialog (clear all state)
      handleCloseRequestDialog();
      
      // Show success message
      setSnackbarMessage('Your request has been sent. Wait for your manager\'s approval.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('Error submitting permission request:', error);
      setSnackbarMessage('Failed to submit permission request');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle closing the request dialog - clear all dialog state
  const handleCloseRequestDialog = () => {
    setRequestDialogOpen(false);
    setDialogsOpen(prev => ({ ...prev, request: false }));
    setRequestedModelIds([]);
    setRequestedAgentIds([]);
    setRequestedFeatureIds([]);
    setPermissionRequestReason('');
    setAllModels([]);
    setAllAgents([]);
    setAllFeatures([]);
  };

  const handleSaveCustomInstructions = async () => {
    try {
      // Validate length
      if (customInstructions.length > customInstructionsMaxLength) {
        setSnackbarMessage(`Custom instructions cannot exceed ${customInstructionsMaxLength} characters`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      // Update settings
      const updatedSettings = await updateCurrentUserSettingsMutation.mutateAsync({
        custom_instructions: customInstructions.trim() || undefined,
        custom_instructions_enabled: customInstructionsEnabled
      });
      
      // Update local state
      setUserSettings(updatedSettings);
      setCustomInstructions(updatedSettings.custom_instructions || '');
      setCustomInstructionsEnabled(updatedSettings.custom_instructions_enabled || false);
      
      setSnackbarMessage('Custom instructions saved successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving custom instructions:', error);
      setSnackbarMessage('Failed to save custom instructions');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleRequestShareProfileToggle = (nextValue: boolean) => {
    setPendingShareProfileEnabled(nextValue);
    setShareProfileDialogOpen(true);
  };

  const handleCloseShareProfileDialog = () => {
    setShareProfileDialogOpen(false);
  };

  const handleConfirmShareProfileToggle = async () => {
    try {
      const updatedSettings = await updateCurrentUserSettingsMutation.mutateAsync({
        share_profile_enabled: pendingShareProfileEnabled,
      });

      setUserSettings(updatedSettings);
      setShareProfileEnabled(updatedSettings.share_profile_enabled || false);
      setSnackbarMessage(
        pendingShareProfileEnabled
          ? 'Profile sharing with Bolt enabled'
          : 'Profile sharing with Bolt disabled'
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating profile sharing preference:', error);
      setSnackbarMessage('Failed to update profile sharing preference');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setShareProfileDialogOpen(false);
    }
  };
  
  const handleToggleModelRequest = (modelId: string) => {
    setRequestedModelIds(prev => 
      prev.includes(modelId) ? prev.filter(id => id !== modelId) : [...prev, modelId]
    );
  };
  
  const handleToggleAgentRequest = (agentId: string) => {
    setRequestedAgentIds(prev => 
      prev.includes(agentId) ? prev.filter(id => id !== agentId) : [...prev, agentId]
    );
  };
  
  const handleToggleFeatureRequest = (featureId: string) => {
    setRequestedFeatureIds(prev => 
      prev.includes(featureId) ? prev.filter(id => id !== featureId) : [...prev, featureId]
    );
  };
  
  // Get non-accessible permissions (filter out already accessible ones)
  const nonAccessibleModels = allModels.filter(m => 
    !availableModels.some(am => am.id === m.id) && !m.is_implicit
  );
  const nonAccessibleAgents = allAgents.filter(a => 
    !availableAgents.some(aa => aa.id === a.id) && !a.is_implicit
  );
  const nonAccessibleFeatures = allFeatures.filter(f => 
    !availableFeatures.some(af => af.id === f.id) && !f.is_implicit
  );
  const hasRequestedPermissions =
    requestedModelIds.length > 0 ||
    requestedAgentIds.length > 0 ||
    requestedFeatureIds.length > 0;
  const hasRequestReason = permissionRequestReason.trim().length > 0;

  // Common card style
  const cardStyle = {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    height: 300,
    minHeight: 300
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'auto',
      }}
    >
      {/* Header with back button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          height: '45px',
        //   minHeight: '46px',
        //   maxHeight: '46px',
          px: { xs: 0.5, sm: 1.5 },
          py: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={() => navigate('/')}
          sx={{
            color: 'primary.main',
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          User Settings
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          aria-label="user settings tabs"
          sx={{
            px: { xs: 0.5, sm: 1.5 },
          }}
        >
          <Tab label="My Profile" {...a11yProps(0)} />
          <Tab label="Permissions" {...a11yProps(1)} />
          <Tab label="Custom Instructions" {...a11yProps(2)} />
          <Tab label="Dashboard" {...a11yProps(3)} />
        </Tabs>
      </Box>

      {/* Content area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        {showStaleDataWarning ? (
          <Box sx={{ p: 3, pb: 0 }}>
            <QueryStaleDataWarning
              message="Showing last synced settings. We couldn't refresh your latest profile data."
              onRetry={() => {
                void retryUserSettingsQuery();
              }}
            />
          </Box>
        ) : null}

        {blockingUserSettingsError ? (
          <Box sx={{ p: 3 }}>
            <QueryErrorState
              title="Unable to load user settings"
              message={blockingUserSettingsError}
              onRetry={() => {
                void retryUserSettingsQuery();
              }}
              minHeight={260}
            />
          </Box>
        ) : (
        <>
        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Profile Picture */}
              {profileAvatarUrl && (
                <Box display="flex" justifyContent="center" mb={3}>
                  <Avatar
                    src={profileAvatarUrl}
                    alt={user?.name || 'User'}
                    sx={{ width: 150, height: 150 }}
                  />
                </Box>
              )}

              {/* Profile Fields */}
              <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                <TextField
                  label="Name"
                  value={userSettings?.name || user?.name || ''}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input:{ readOnly: true }
                  }}
                />
                
                <TextField
                  label="Email"
                  value={userSettings?.email || ''}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input:{ readOnly: true }
                  }}
                />

                <TextField
                  label="Job Title"
                  value={userSettings?.job_title || 'Unassigned'}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input:{ readOnly: true }
                  }}
                />

                <TextField
                  label="Manager"
                  value={userSettings?.manager_name || 'Not assigned'}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input:{ readOnly: true }
                  }}
                />
                
                <TextField
                  label="Business Unit"
                  value={userSettings?.business_unit || 'Unassigned'}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input:{ readOnly: true }
                  }}
                />

                <TextField
                  label="Department"
                  value={userSettings?.department || 'Unassigned'}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input:{ readOnly: true }
                  }}
                />

                <TextField
                  label="Office Location"
                  value={userSettings?.office_location || 'Unassigned'}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input:{ readOnly: true }
                  }}
                />
                
                <TextField
                  label="Added Date"
                  value={userSettings?.added_date ? new Date(userSettings.added_date).toLocaleString() : 'N/A'}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input:{ readOnly: true }
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={shareProfileEnabled}
                      onChange={(e) => handleRequestShareProfileToggle(e.target.checked)}
                      disabled={updateCurrentUserSettingsMutation.isPending}
                      color="primary"
                    />
                  }
                  label= "Share my profile with Bolt" 
                  sx={{ mb: 3 }}
                />
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Permissions Tab */}
        <TabPanel value={tabValue} index={1}>
          {permissionsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              {/* Request Permissions Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenRequestDialog}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Request Permissions
                </Button>
              </Box>

              <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                <PermissionsList
                  title="Model Permissions"
                  available={availableModels}
                  allItems={allModels}
                  requestedIds={requestedModelIds}
                  onToggleRequest={handleToggleModelRequest}
                  compact
                />

                <PermissionsList
                  title="Agent Permissions"
                  available={availableAgents}
                  allItems={allAgents}
                  requestedIds={requestedAgentIds}
                  onToggleRequest={handleToggleAgentRequest}
                  compact
                />

                <PermissionsList
                  title="Feature Permissions"
                  available={availableFeatures}
                  allItems={allFeatures}
                  requestedIds={requestedFeatureIds}
                  onToggleRequest={handleToggleFeatureRequest}
                  compact
                />
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Custom Instructions Tab */}
        <TabPanel value={tabValue} index={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Custom Instructions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Personalize how Bolt responds by providing custom instructions. These preferences will be applied to all your conversations, helping Bolt tailor its responses to your specific needs and communication style.
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={customInstructionsEnabled}
                    onChange={(e) => setCustomInstructionsEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label={customInstructionsEnabled 
                  ? "Custom instructions are enabled" 
                  : "Custom instructions are disabled"}
                sx={{ mb: 3 }}
              />
              
              <TextField
                label="Your Custom Instructions"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                fullWidth
                multiline
                rows={10}
                margin="normal"
                variant="outlined"
                placeholder="Enter your preferences, style guidelines or specific instructions for how Bolt should respond. Examples: Preferred communication style (formal, casual, technical), specific formats for responses, language preferences, topics to prioritize or avoid, any other personalization preferences."
                disabled={!customInstructionsEnabled}
                helperText={`${customInstructions.length}/${customInstructionsMaxLength} characters`}
                error={customInstructions.length > customInstructionsMaxLength}
              />
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveCustomInstructions}
                  disabled={savingCustomInstructions || customInstructions.length > customInstructionsMaxLength}
                >
                  {savingCustomInstructions ? <CircularProgress size={24} /> : 'Save Custom Instructions'}
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Dashboard Tab */}
        <TabPanel value={tabValue} index={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              flexWrap: 'wrap',
              gap: 2
              }}>
              {/* Date Range Selector */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      }
                    }
                  }
                }}
              />
              <Typography sx={{ color: '#94a3b8' }}>to</Typography>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      }
                    }
                  }
                }}
              />
              </Box>
            </Box>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3}}>
              <Box 
                ref={dashboardRef}
                className="dashboard-print"
                sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 3,
                  '& > *': {
                    flex: '1',
                    maxWidth: 600,
                    minWidth: 400
                  }
                }}
              >

              {/* Total Messages Card */}
              <Card sx={cardStyle}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 5 }}>
                    Total Messages
                  </Typography>
                  {loadingMyTotalMessages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                      <CircularProgress />
                    </Box>
                  ) : errorMyTotalMessages ? (
                    <Alert severity="error">{errorMyTotalMessages.message}</Alert>
                  ) : !myTotalMessages ? (
                    <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                      No data available for the selected date range
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ color: '#fff', borderBottom: '1px solid #334155', fontSize: '1rem' }}>
                              📥 Received Messages
                            </TableCell>
                            <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', textAlign: 'right', fontSize: '1.1rem', fontWeight: 'bold' }}>
                              {myTotalMessages.assistant_messages.toLocaleString()}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#fff', borderBottom: '1px solid #334155', fontSize: '1rem' }}>
                              📤 Sent Messages
                            </TableCell>
                            <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', textAlign: 'right', fontSize: '1.1rem', fontWeight: 'bold' }}>
                              {myTotalMessages.user_messages.toLocaleString()}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', fontSize: '1.1rem', fontWeight: 'bold' }}>
                              Total
                            </TableCell>
                            <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }}>
                              {myTotalMessages.total_messages.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
              
                {/* Model Usage Table */}
              <Card sx={cardStyle}>
                <CardContent sx={{ height: '80%' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 5 }}>
                    Models Used Most
                  </Typography>
                  {loadingMyModels ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: "100%" }}>
                      <CircularProgress />
                    </Box>) : errorMyModels ? (
                    <Alert severity="error">{errorMyModels.message}</Alert>
                  ) : !myModelsUsage ? (
                    <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                      No data available for the selected date range
                    </Typography>
                  ) : (
                  <ResponsiveContainer width="100%">
                    <BarChart data={myModelsUsage?.map(model => ({ name: model.model_name, requests: model.request_count }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis 
                        dataKey="name" 
                        stroke="#94a3b8"
                        fontSize="0.9rem"
                        tick={{ fill: '#94a3b8' }}
                      />
                      <YAxis 
                        stroke="#94a3b8"
                        tick={{ fill: '#94a3b8' }}
                        label={{ value: 'Requests', angle: 0, position: 'top', fill: '#94a3b8', offset: 10 }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value: any) => [value.toLocaleString(), 'Requests']}
                      />
                      <Bar dataKey="requests" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>)}
                </CardContent>
              </Card>

              {/** Total Costs Table */}
              <Card sx={cardStyle}>
                <CardContent sx={{ height: '80%' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 2 }}>
                    Total Costs (€)
                  </Typography>
                  {loadingTotalCosts ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: "100%" }}>
                    <CircularProgress />
                  </Box>
                  ) : errorTotalCosts ? (
                    <Alert severity="error">{errorTotalCosts.message}</Alert>
                  ) : !myTotalCosts ? (
                    <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                      No data available for the selected date range
                    </Typography>
                  ) : (
                  <TableContainer>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ color: '#fff', borderBottom: '1px solid #334155', fontSize: '1rem' }}>
                            ➡️ Total Input Tokens
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', textAlign: 'right', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {myTotalCosts.total_tokens_input.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#fff', borderBottom: '1px solid #334155', fontSize: '1rem' }}>
                            ⬅️ Total Output Tokens
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', textAlign: 'right', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {myTotalCosts.total_tokens_output.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', fontSize: '1.1rem', fontWeight: 'bold' }}>
                           🧠 Total Tokens
                          </TableCell>
                          <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {myTotalCosts.total_tokens.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            💰 Total Cost
                          </TableCell>
                          <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {myTotalCosts.total_cost.toLocaleString()} €
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}
                </CardContent>
              </Card>

              {/* Monthly Costs Card */}
              <Card sx={cardStyle}>
                <CardContent sx={{ height: '80%' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 5 }}>
                    My Monthly Costs (€)
                  </Typography>
                  {loadingMonthlyCosts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: "100%" }}>
                      <CircularProgress />
                    </Box>) : errorMonthlyCosts ? (
                      <Alert severity="error">{errorMonthlyCosts.message}</Alert>
                      ) : !myMonthlyCosts ? (
                      <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                        No data available for the selected date range
                      </Typography>
                    ) : (
                    <ResponsiveContainer width="100%">
                    <LineChart data={myMonthlyCosts}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#94a3b8"
                        fontSize="0.9rem"
                        tick={{ fill: '#94a3b8' }}
                      />
                      <YAxis 
                        stroke="#94a3b8"
                        tick={{ fill: '#94a3b8' }}
                        hide
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value: any) => [`${value.toLocaleString()} €`, 'Cost']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="cost" 
                        stroke="#60a5fa" 
                        strokeWidth={3}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>)}
                </CardContent>
              </Card>

              </Box>
            </Box>
          </LocalizationProvider>
        </TabPanel>

        </>
        )}
      </Box>
      
      {/* Request Permissions Dialog */}
      <DialogWrapper open={requestDialogOpen} onClose={handleCloseRequestDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Request Permissions
          <IconButton
            aria-label="close"
            onClick={handleCloseRequestDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the permissions you would like to request. Your manager will be notified.
          </Typography>
          
          <PermissionsList
            title="Model Permissions"
            available={availableModels}
            allItems={nonAccessibleModels.concat(availableModels)}
            pendingIds={pendingModelIds}
            requestedIds={requestedModelIds}
            onToggleRequest={handleToggleModelRequest}
            compact
          />
          
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2">
              Need a model that's not listed?{' '}
              <Typography
                component="a"
                href="https://bosnet.sharepoint.com/:l:/s/Innovation_Technology/FFE6eZj93KNHlm73tyXCGtUB8IClFgU6BeetsuMpiKHE6A?nav=MmM1MGMxMDYtNTE2Ny00MWJlLThkNzctYjgzOTc1NjRjZjRk"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'inline',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Request for adding model in BOLT!
              </Typography>
            </Typography>
          </Box>

          <PermissionsList
            title="Agent Permissions"
            available={availableAgents}
            allItems={nonAccessibleAgents.concat(availableAgents)}
            pendingIds={pendingAgentIds}
            requestedIds={requestedAgentIds}
            onToggleRequest={handleToggleAgentRequest}
            compact
          />

          <PermissionsList
            title="Feature Permissions"
            available={availableFeatures}
            allItems={nonAccessibleFeatures.concat(availableFeatures)}
            pendingIds={pendingFeatureIds}
            requestedIds={requestedFeatureIds}
            onToggleRequest={handleToggleFeatureRequest}
            compact
          />
          
          {nonAccessibleModels.length === 0 && nonAccessibleAgents.length === 0 && nonAccessibleFeatures.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              You already have access to all available permissions.
            </Typography>
          )}

          {hasRequestedPermissions && (
            <TextField
              label="Reason for request"
              value={permissionRequestReason}
              onChange={(e) => setPermissionRequestReason(e.target.value)}
              fullWidth
              required
              multiline
              minRows={3}
              margin="normal"
              variant="outlined"
              placeholder="Explain why you need these permissions"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequestDialog} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitRequest} 
            variant="contained"
            disabled={submitting || !hasRequestedPermissions || !hasRequestReason}
          >
            {submitting ? 'Submitting...' : 'Send Request'}
          </Button>
        </DialogActions>
      </DialogWrapper>

      {/* Share Profile Confirmation Dialog */}
      <DialogWrapper open={shareProfileDialogOpen} onClose={handleCloseShareProfileDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Confirm Profile Sharing
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {pendingShareProfileEnabled
              ? 'Enable profile sharing with Bolt? Your profile details from this tab will be available to agents to personalize responses.'
              : 'Disable profile sharing with Bolt? Agents will stop receiving profile details from this tab.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareProfileDialog} variant="outlined" disabled={updateCurrentUserSettingsMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirmShareProfileToggle} variant="contained" disabled={updateCurrentUserSettingsMutation.isPending}>
            {updateCurrentUserSettingsMutation.isPending ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </DialogWrapper>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserSettingsPage;
